import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, ArrowUp } from 'lucide-react';

interface TutorialStep {
    title: string;
    text: string;
    position: 'center' | 'bottom' | 'top-right' | 'top-left' | 'top-center' | 'top-left-no-arrow';
    hasButton?: boolean;
}

interface TutorialGuideProps {
    step: TutorialStep;
    onNext?: () => void;
    onEnd?: () => void;
}

const positionClasses = {
    center: 'items-center justify-center',
    bottom: 'items-end justify-center pb-48', // Position above the troop selector
    'top-right': 'items-start justify-end pt-24 pr-4 text-right',
    'top-left': 'items-start justify-start pt-24 pl-4 text-left',
    'top-center': 'items-start justify-center pt-12',
    'top-left-no-arrow': 'items-start justify-start pt-24 pl-4 text-left',
};

const arrowPosition: { [key: string]: React.ReactNode } = {
    center: null,
    bottom: <ArrowDown className="w-12 h-12 text-yellow-400 mt-4 animate-bounce" />,
    'top-right': <ArrowUp className="w-12 h-12 text-yellow-400 mb-4 animate-bounce" />,
    'top-left': <ArrowDown className="w-12 h-12 text-yellow-400 mt-4 animate-bounce" />,
    'top-center': <ArrowDown className="w-12 h-12 text-yellow-400 mt-4 animate-bounce" />,
    'top-left-no-arrow': null,
}

export function TutorialGuide({ step, onNext, onEnd }: TutorialGuideProps) {
    const isFinalStep = step.hasButton && !onNext && !!onEnd;

    // Only the tutorial box itself should have pointer events, not the whole overlay
    // This allows the rest of the canvas to remain interactive, especially for 'top-left' and 'top-right' positions
    return (
        <div className={`absolute inset-0 flex ${positionClasses[step.position]} pointer-events-none z-50`}>
            <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="bg-gray-900/90 border-2 border-yellow-400 rounded-lg p-6 text-white max-w-md shadow-2xl pointer-events-auto flex flex-col items-center"
            >
                {step.position === 'top-right' && arrowPosition[step.position]}
                <h3 className="text-2xl font-bold text-yellow-400 mb-3 text-center">{step.title}</h3>
                <p className="text-lg mb-5 text-center">{step.text}</p>
                {step.hasButton && (
                    <button
                        onClick={isFinalStep ? onEnd : onNext}
                        className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold py-2 px-6 rounded-lg text-xl transition-transform transform hover:scale-105"
                    >
                        {isFinalStep ? "Finish Tutorial" : "Next"}
                    </button>
                )}
                {step.position !== 'top-right' && arrowPosition[step.position]}
            </motion.div>
        </div>
    );
}
